/**
 * Company-scoped diagnostics page.
 *
 * Accessible to super-admins after setting their active org to the target company.
 * Shows diagnostic events scoped to that company plus a meeting timeline viewer.
 *
 * Data fetching goes through Eden Treaty (the company-scoped diagnostics client
 * via useCompanyQuery); the events + timeline tables render through the shared
 * DiagnosticEventsTable, the same component the system diagnostics view uses.
 *
 * Route: /_auth/companies/$id/diagnostics
 * The trailing underscore on the filename ($id_.diagnostics) means TanStack Router
 * treats $id as a non-layout parent (no $id layout wrapper applies).
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Container, Group, Paper, Select, Stack, Text, TextInput, Title } from "@mantine/core"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { DiagnosticEventsTable } from "../../../components/DiagnosticEventsTable"
import { useCompanyQuery } from "../../../hooks/useCompanyQuery"
import { extractErrorMessage, unwrapData } from "../../../lib/api/errors"
import { MSG_NO_EVENTS } from "../../../lib/messages"
import { queryKeys } from "../../../lib/query-keys"

function CompanyDiagnosticsPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const { i18n } = useLingui()

  // Events filter state
  const [filterMeetingId, setFilterMeetingId] = useState("")
  const [filterSeverity, setFilterSeverity] = useState<string | null>(null)
  const [filterSource, setFilterSource] = useState<string | null>(null)
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null)

  // Meeting timeline state
  const [timelineMeetingId, setTimelineMeetingId] = useState("")
  const [timelineInput, setTimelineInput] = useState("")

  const eventsQuery = useCompanyQuery({
    // Admin key: the explicit target company id, not the active org.
    key: () => queryKeys.admin.companyDiagnosticsEvents(id, filterMeetingId, filterSeverity, filterSource),
    queryFn: async (company) => {
      const query: { meetingId?: string; severity?: string; source?: string } = {}
      if (filterMeetingId) query.meetingId = filterMeetingId
      if (filterSeverity) query.severity = filterSeverity
      if (filterSource) query.source = filterSource
      const result = await company.diagnostics.events.get({ query })
      return unwrapData(result)
    },
    refetchInterval: 30_000,
  })

  const timelineQuery = useCompanyQuery({
    key: () => queryKeys.admin.companyDiagnosticsTimeline(id, timelineMeetingId),
    queryFn: async (company) => {
      const result = await company.diagnostics.events({ meetingId: timelineMeetingId }).timeline.get()
      return unwrapData(result)
    },
    enabled: timelineMeetingId.length > 0,
  })

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="lg">
        <Group>
          <Link to="/companies/$id" params={{ id }}>
            &larr; <Trans>Company</Trans>
          </Link>
          <Title order={2}>
            <Trans>Company diagnostics</Trans>
          </Title>
        </Group>
      </Group>

      <Stack gap="xl">
        {/* Events */}
        <Paper withBorder p="md">
          <Text fw={600} mb="md">
            <Trans>Diagnostic events</Trans>
          </Text>

          <Group gap="sm" mb="md" wrap="wrap">
            <TextInput
              placeholder={i18n._(msg`Meeting ID`)}
              value={filterMeetingId}
              onChange={(e) => setFilterMeetingId(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <Select
              placeholder={i18n._(msg`Severity`)}
              value={filterSeverity}
              onChange={setFilterSeverity}
              clearable
              data={["debug", "info", "warn", "error"]}
              style={{ minWidth: 120 }}
            />
            <Select
              placeholder={i18n._(msg`Source`)}
              value={filterSource}
              onChange={setFilterSource}
              clearable
              data={["server", "client"]}
              style={{ minWidth: 120 }}
            />
            <Button size="compact-sm" variant="subtle" onClick={() => eventsQuery.refetch()}>
              <Trans>Refresh</Trans>
            </Button>
          </Group>

          <Text size="xs" c="dimmed" mb="sm">
            <Trans>{eventsQuery.data?.total ?? 0} event(s) found</Trans>
          </Text>

          {eventsQuery.isError && (
            <Text c="danger" size="sm" mb="sm">
              <Trans>Failed to load events: {extractErrorMessage(eventsQuery.error)}</Trans>
            </Text>
          )}

          {(eventsQuery.data?.events ?? []).length === 0 && !eventsQuery.isError ? (
            <Text size="sm" c="dimmed">
              {MSG_NO_EVENTS}
            </Text>
          ) : (
            <DiagnosticEventsTable
              events={eventsQuery.data?.events ?? []}
              expandedEventId={expandedEventId}
              onToggleEvent={(evId) => setExpandedEventId(expandedEventId === evId ? null : evId)}
              withSeconds
            />
          )}
        </Paper>

        {/* Meeting Timeline */}
        <Paper withBorder p="md">
          <Text fw={600} mb="md">
            <Trans>Meeting timeline</Trans>
          </Text>
          <Group gap="sm" mb="md">
            <TextInput
              placeholder={i18n._(msg`Enter meeting ID`)}
              value={timelineInput}
              onChange={(e) => setTimelineInput(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              variant="light"
              onClick={() => setTimelineMeetingId(timelineInput.trim())}
              disabled={timelineInput.trim().length === 0}
            >
              <Trans>Load timeline</Trans>
            </Button>
          </Group>

          {timelineQuery.isError && (
            <Text c="danger" size="sm" mb="sm">
              <Trans>Failed to load timeline: {extractErrorMessage(timelineQuery.error)}</Trans>
            </Text>
          )}

          {timelineMeetingId && (timelineQuery.data?.events ?? []).length === 0 && !timelineQuery.isError && (
            <Text size="sm" c="dimmed">
              {MSG_NO_EVENTS}
            </Text>
          )}

          {(timelineQuery.data?.events ?? []).length > 0 && (
            <DiagnosticEventsTable events={timelineQuery.data?.events ?? []} withSeconds />
          )}
        </Paper>
      </Stack>
    </Container>
  )
}

export const Route = createFileRoute("/_auth/companies/$id_/diagnostics")({
  component: CompanyDiagnosticsPage,
})
